import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { RefreshCw, Search, Download, TrendingUp, Package, Tag } from 'lucide-react';

const fmt = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-ZA');

const AdminSalesReport = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [groupBy, setGroupBy] = useState('product'); // product | category | subcategory
  const [range, setRange] = useState('30days');       // today | 7days | 30days | 90days | custom
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [appliedCustom, setAppliedCustom] = useState(null);
  const [sortBy, setSortBy] = useState('revenue');    // revenue | units | name
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [paidOnly, setPaidOnly] = useState(true);
  const [totals, setTotals] = useState({ revenue: 0, units: 0, lines: 0 });

  const fetchAllRows = async (table, select, filters) => {
    const PAGE = 1000; let all = []; let from = 0; let more = true;
    while (more) {
      let q = supabase.from(table).select(select).range(from, from + PAGE - 1);
      if (filters) q = filters(q);
      const { data, error } = await q;
      if (error) throw error;
      all = all.concat(data || []);
      more = data && data.length === PAGE;
      from += PAGE;
    }
    return all;
  };

  const load = async () => {
    setLoading(true);
    try {
      // Determine date window
      let startDate = new Date();
      let endDate = new Date();
      if (range === 'custom' && appliedCustom) {
        startDate = new Date(appliedCustom.from); startDate.setHours(0,0,0,0);
        endDate = new Date(appliedCustom.to); endDate.setHours(23,59,59,999);
      } else {
        if (range === 'today') startDate.setHours(0,0,0,0);
        else if (range === '7days') startDate.setDate(startDate.getDate() - 7);
        else if (range === '30days') startDate.setDate(startDate.getDate() - 30);
        else if (range === '90days') startDate.setDate(startDate.getDate() - 90);
      }

      // 1. Get qualifying orders in the period (paid-only by default)
      const orders = await fetchAllRows('orders', 'id, payment_status, created_at', (q) => {
        q = q.gte('created_at', startDate.toISOString()).lte('created_at', endDate.toISOString());
        if (paidOnly) q = q.eq('payment_status', 'paid');
        return q;
      });
      const orderIds = new Set(orders.map(o => o.id));
      if (orderIds.size === 0) { setRows([]); setTotals({ revenue: 0, units: 0, lines: 0 }); setLoading(false); return; }

      // 2. Get order_items for those orders (paginate; filter to our order ids in chunks)
      const orderIdArr = [...orderIds];
      let items = [];
      for (let i = 0; i < orderIdArr.length; i += 300) {
        const chunk = orderIdArr.slice(i, i + 300);
        const part = await fetchAllRows('order_items',
          'order_id, product_id, quantity, picked_quantity, price, promo_price, subtotal',
          (q) => q.in('order_id', chunk));
        items = items.concat(part);
      }

      // 3. Get product category/subcategory/name for the products involved
      const productIds = [...new Set(items.map(it => it.product_id).filter(Boolean))];
      const prodMap = {};
      for (let i = 0; i < productIds.length; i += 300) {
        const chunk = productIds.slice(i, i + 300);
        const { data: prods } = await supabase.from('products')
          .select('id, name, category, subcategory').in('id', chunk);
        (prods || []).forEach(p => { prodMap[p.id] = p; });
      }

      // 4. Aggregate by the chosen grouping. Revenue = picked_quantity (actual fulfilled) * price.
      //    Units = picked_quantity. Falls back to quantity if picked_quantity is null.
      const agg = {};
      let tRev = 0, tUnits = 0, tLines = 0;
      for (const it of items) {
        const p = prodMap[it.product_id] || {};
        const key = groupBy === 'product' ? (p.name || '(unknown product)')
                  : groupBy === 'category' ? (p.category || '(uncategorised)')
                  : (p.subcategory || '(no subcategory)');
        const qty = (it.picked_quantity != null ? it.picked_quantity : it.quantity) || 0;
        const unitPrice = Number(it.price) || 0;
        const revenue = qty * unitPrice;
        if (!agg[key]) agg[key] = { key, units: 0, revenue: 0, lines: 0,
          category: p.category || '', subcategory: p.subcategory || '' };
        agg[key].units += qty;
        agg[key].revenue += revenue;
        agg[key].lines += 1;
        tRev += revenue; tUnits += qty; tLines += 1;
      }
      setRows(Object.values(agg));
      setTotals({ revenue: tRev, units: tUnits, lines: tLines });
    } catch (e) {
      console.error('Sales report error:', e);
      alert('Failed to load sales report: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile) load();
    // eslint-disable-next-line
  }, [userProfile, groupBy, range, appliedCustom, paidOnly]);

  const filtered = rows
    .filter(r => !search || r.key.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'revenue') cmp = a.revenue - b.revenue;
      else if (sortBy === 'units') cmp = a.units - b.units;
      else cmp = a.key.localeCompare(b.key);
      return sortDir === 'desc' ? -cmp : cmp;
    });

  const exportCSV = () => {
    const header = [groupBy, 'Units', 'Revenue', '% of Revenue', 'Line count'];
    const lines = filtered.map(r => [
      `"${r.key.replace(/"/g,'""')}"`, r.units, r.revenue.toFixed(2),
      totals.revenue ? ((r.revenue / totals.revenue) * 100).toFixed(1) + '%' : '0%', r.lines
    ].join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `sales_by_${groupBy}_${range}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const rangeLabel = range === 'custom' && appliedCustom
    ? `${appliedCustom.from} to ${appliedCustom.to}`
    : ({ today: 'Today', '7days': 'Last 7 days', '30days': 'Last 30 days', '90days': 'Last 90 days' }[range]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Report</h1>
          <p className="text-gray-600 mt-1">Sales by {groupBy} · {rangeLabel} · {paidOnly ? 'paid orders only' : 'all orders'}</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          <RefreshCw size={18} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 my-4">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total revenue</p>
          <p className="text-2xl font-bold text-green-600">{fmt(totals.revenue)}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Units sold</p>
          <p className="text-2xl font-bold text-gray-900">{fmtNum(totals.units)}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">{groupBy === 'product' ? 'Distinct products' : groupBy === 'category' ? 'Categories' : 'Subcategories'}</p>
          <p className="text-2xl font-bold text-gray-900">{rows.length}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Group by */}
        <div className="flex rounded-lg border overflow-hidden">
          {[['product','Product',Package],['category','Category',Tag],['subcategory','Subcategory',TrendingUp]].map(([val,label,Icon]) => (
            <button key={val} onClick={() => setGroupBy(val)}
              className={`flex items-center gap-1 px-3 py-2 text-sm ${groupBy===val ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Period */}
        <select value={range} onChange={e => { setRange(e.target.value); if (e.target.value !== 'custom') setAppliedCustom(null); }}
          className="border rounded-lg px-3 py-2 text-sm">
          <option value="today">Today</option>
          <option value="7days">Last 7 days</option>
          <option value="30days">Last 30 days</option>
          <option value="90days">Last 90 days</option>
          <option value="custom">Custom range…</option>
        </select>

        {range === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo || undefined} onChange={e => setCustomFrom(e.target.value)} className="border rounded-lg px-2 py-2 text-sm" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={customTo} min={customFrom || undefined} onChange={e => setCustomTo(e.target.value)} className="border rounded-lg px-2 py-2 text-sm" />
            <button onClick={() => { if (customFrom && customTo) setAppliedCustom({ from: customFrom, to: customTo }); }}
              disabled={!customFrom || !customTo}
              className={`px-3 py-2 rounded-lg text-sm text-white ${customFrom && customTo ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300'}`}>Go</button>
          </div>
        )}

        {/* Paid only */}
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={paidOnly} onChange={e => setPaidOnly(e.target.checked)} /> Paid orders only
        </label>

        {/* Search */}
        <div className="flex items-center gap-2 flex-1 min-w-[180px] bg-white border rounded-lg px-3 py-2">
          <Search size={16} className="text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${groupBy}…`} className="flex-1 outline-none text-sm" />
        </div>

        <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
          <Download size={16} /> CSV
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2 cursor-pointer" onClick={() => toggleSort('name')}>
                  {groupBy === 'product' ? 'Product' : groupBy === 'category' ? 'Category' : 'Subcategory'} {sortBy==='name' ? (sortDir==='desc'?'↓':'↑') : ''}
                </th>
                <th className="text-right px-4 py-2 cursor-pointer" onClick={() => toggleSort('units')}>
                  Units {sortBy==='units' ? (sortDir==='desc'?'↓':'↑') : ''}
                </th>
                <th className="text-right px-4 py-2 cursor-pointer" onClick={() => toggleSort('revenue')}>
                  Revenue {sortBy==='revenue' ? (sortDir==='desc'?'↓':'↑') : ''}
                </th>
                <th className="text-right px-4 py-2">% of revenue</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-900">{r.key}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmtNum(r.units)}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{fmt(r.revenue)}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{totals.revenue ? ((r.revenue/totals.revenue)*100).toFixed(1) : '0.0'}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="p-6 text-gray-500 text-center">No sales in this period</p>}
        </div>
      )}
    </div>
  );
};

export default AdminSalesReport;
